import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, X, ChevronRight } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatINR } from "@/lib/format";
import { resolveMediaUrl, handleMediaError } from "@/lib/media";
import { BusinessName } from "./common";

function SearchBox({ testid, q, setQ, suggest, boxRef, submitSearch, navigate, setSuggest, overHero }) {
  // Gooey focus treatment: a blurred amber blob blooms behind the pill and the pill
  // grows a touch when the field gains focus. Pure CSS (group-focus-within) so the
  // form, input, debounce and suggestions below keep working exactly as before.
  const iconTone = overHero ? "text-white/60 group-focus-within/search:text-amber-300" : "text-slate-400 group-focus-within/search:text-amber-500";
  const fieldTone = overHero
    ? "bg-white/15 border-white/25 text-white placeholder:text-white/60 focus:bg-white/25 focus:border-amber-300"
    : "bg-slate-100 border-transparent text-slate-900 focus:bg-white focus:border-amber-400";
  return (
    <div ref={boxRef} className="relative w-full">
      <form onSubmit={submitSearch} role="search" className="group/search relative">
        <span aria-hidden="true" data-testid="search-glow"
          className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-r from-amber-300/40 via-amber-200/25 to-amber-300/40 blur-md scale-90 opacity-0 transition-all duration-300 group-focus-within/search:scale-110 group-focus-within/search:opacity-100 motion-reduce:transition-none" />
        <Search className={`absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors motion-reduce:transition-none ${iconTone}`} />
        <input
          data-testid={testid}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search earbuds, chargers, cases…"
          aria-label="Search products"
          className={`relative z-10 w-full h-11 pl-10 pr-4 rounded-full border text-sm outline-none transition-all duration-300 focus:ring-4 focus:ring-amber-300/35 group-focus-within/search:scale-[1.015] motion-reduce:transition-none ${fieldTone}`}
        />
      </form>
      {suggest && (suggest.products.length || suggest.categories.length || suggest.brands.length) > 0 && (
        <div className="absolute z-50 mt-2 w-full bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden" data-testid="search-suggestions">
          {suggest.products.map((p) => (
            <button key={p.id} onMouseDown={() => { navigate(`/product/${p.slug}`); setSuggest(null); setQ(""); }}
              className="flex items-center gap-3 w-full px-3 py-2.5 hover:bg-slate-50 text-left">
              <img src={resolveMediaUrl(p.images?.[0])} alt={p.name} onError={handleMediaError} className="h-10 w-10 rounded object-cover bg-slate-100" />
              <span className="flex-1 text-sm text-slate-700 line-clamp-1">{p.name}</span>
              <span className="text-sm font-mono font-semibold text-slate-900">{formatINR(p.price)}</span>
            </button>
          ))}
          {suggest.categories.map((c) => (
            <button key={c.id} onMouseDown={() => { navigate(`/category/${c.slug}`); setSuggest(null); setQ(""); }}
              className="flex items-center gap-2 w-full px-3 py-2 hover:bg-slate-50 text-left text-xs text-slate-500">
              <Search className="h-3 w-3" /> in <span className="font-medium text-slate-700">{c.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { business, settingsLoading, cartCount, wishlist } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [suggest, setSuggest] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const boxRef = useRef();
  // Interactive navbar spotlight: two CSS custom properties driven from rAF, so
  // pointer movement never triggers a React re-render. It is enabled only for a
  // fine pointer with motion allowed — touch and reduced-motion users simply get
  // the glass navbar with no highlight.
  const headerRef = useRef(null);
  const spotRef = useRef(null);
  const spotRaf = useRef(0);
  const spotEnabled = useRef(false);
  // Over the cinematic hero the bar is a dark 30% glass with white text; over page
  // content it flips to a light glass with the usual slate text. A single boolean,
  // so scrolling never re-renders the header frame by frame.
  const [overHero, setOverHero] = useState(false);
  // A stored logo that fails to load must degrade to the monogram, never a broken
  // image; changing the logo in settings gives it another chance.
  const [logoFailed, setLogoFailed] = useState(false);
  const logo = business?.logo;
  const showLogo = Boolean(logo) && !logoFailed;

  useEffect(() => { setLogoFailed(false); }, [logo]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data.categories))
      .catch((err) => console.error("Header categories unavailable:", err?.message));
  }, []);

  useEffect(() => {
    if (q.length < 2) { setSuggest(null); return; }
    const t = setTimeout(() => {
      api.get(`/search/suggest?q=${encodeURIComponent(q)}`).then(({ data }) => setSuggest(data))
        .catch((err) => console.error("Search suggestions unavailable:", err?.message));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setSuggest(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Decides whether the spotlight may run, and keeps that decision live if the
  // user switches to reduced motion or plugs in a mouse.
  useEffect(() => {
    const mm = (query) => (window.matchMedia
      ? window.matchMedia(query)
      : { matches: false, addEventListener: () => {}, removeEventListener: () => {} });
    const fine = mm("(hover: hover) and (pointer: fine)");
    const still = mm("(prefers-reduced-motion: reduce)");

    const sync = () => {
      spotEnabled.current = fine.matches && !still.matches;
      if (!spotEnabled.current && spotRef.current) spotRef.current.style.opacity = "0";
    };
    sync();
    fine.addEventListener?.("change", sync);
    still.addEventListener?.("change", sync);
    return () => {
      fine.removeEventListener?.("change", sync);
      still.removeEventListener?.("change", sync);
      if (spotRaf.current) cancelAnimationFrame(spotRaf.current);
    };
  }, []);

  // Publish the header's REAL height so full-bleed sections can sit underneath it.
  // It is 64px on desktop but ~120px on mobile (the search row is a second row), so
  // a hardcoded offset would hide the top of the pinned hero on phones.
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const apply = () => document.documentElement.style.setProperty("--header-h", `${el.offsetHeight}px`);
    apply();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(apply) : null;
    if (ro) ro.observe(el);
    window.addEventListener("resize", apply);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, []);

  // Does the bar currently sit over the cinematic hero? Re-measured on scroll,
  // resize and route change (the header survives navigation, the hero does not).
  useEffect(() => {
    let raf = 0;
    const measure = () => {
      raf = 0;
      const el = headerRef.current;
      const hero = document.querySelector('[data-testid="cinematic-hero"]');
      setOverHero(Boolean(el && hero && hero.getBoundingClientRect().bottom > el.offsetHeight + 1));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(measure); };
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [location.pathname]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/search?q=${encodeURIComponent(q.trim())}`); setSuggest(null); }
  };

  const onSpotlightMove = (e) => {
    if (!spotEnabled.current) return;
    const header = headerRef.current;
    const spot = spotRef.current;
    if (!header || !spot || spotRaf.current) return;
    const rect = header.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    spotRaf.current = requestAnimationFrame(() => {
      spotRaf.current = 0;
      spot.style.setProperty("--spot-x", `${x}px`);
      spot.style.setProperty("--spot-y", `${y}px`);
      spot.style.opacity = "1";
    });
  };

  const onSpotlightLeave = () => {
    if (spotRaf.current) { cancelAnimationFrame(spotRaf.current); spotRaf.current = 0; }
    if (spotRef.current) spotRef.current.style.opacity = "0";
  };

  // Theme tokens for the two glass states, so every element stays readable.
  const glass = overHero
    ? "bg-slate-950/30 border-white/15 shadow-[0_8px_32px_-16px_rgba(2,6,23,0.55)]"
    : "bg-white/60 border-slate-900/10 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_10px_28px_-18px_rgba(15,23,42,0.28)]";
  const tone = {
    name: overHero ? "text-white" : "text-slate-900",
    nav: overHero ? "text-white/80" : "text-slate-600",
    navHover: overHero ? "hover:text-white" : "hover:text-slate-900",
    icon: overHero ? "text-white" : "text-slate-700",
    iconHover: overHero ? "hover:bg-white/10" : "hover:bg-slate-100",
    monogram: overHero ? "bg-white text-slate-900" : "bg-slate-900 text-white",
  };

  return (
    <>
      <header
        ref={headerRef}
        onPointerMove={onSpotlightMove}
        onPointerLeave={onSpotlightLeave}
        data-testid="site-header"
        data-over-hero={overHero ? "true" : "false"}
        className={`sticky top-0 z-40 border-b backdrop-blur-2xl backdrop-saturate-150 transition-colors duration-300 motion-reduce:transition-none ${glass}`}
      >
        {/* Cursor-following spotlight. pointer-events-none + z-0 keeps every link,
            input and dropdown above it; opacity stays 0 unless a fine pointer is
            present and motion is allowed. */}
        <div
          ref={spotRef}
          aria-hidden="true"
          data-testid="nav-spotlight"
          className="pointer-events-none absolute inset-0 z-0 opacity-0 transition-opacity duration-300 motion-reduce:transition-none"
          style={{ background: `radial-gradient(360px circle at var(--spot-x, 50%) var(--spot-y, 0px), ${overHero ? "rgba(251,191,36,0.22)" : "rgba(245,158,11,0.16)"}, transparent 70%)` }}
        />
        <div className="container-x relative z-10">
          <div className="h-16 flex items-center gap-2 sm:gap-4">
            <button className="md:hidden p-1.5 -ml-1 rounded-full transition-colors" onClick={() => setMenuOpen(true)} data-testid="mobile-menu-btn" aria-label="Menu">
              <Menu className={`h-6 w-6 ${tone.icon}`} />
            </button>
            <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
              {/* The logo is configurable in Admin → Settings (uploaded file or pasted
                  URL). Until one is set — or if it fails to load — the monogram
                  placeholder is shown, so the header is never left with a broken
                  image. */}
              {showLogo ? (
                <img src={resolveMediaUrl(business.logo)} alt={business?.name ? `${business.name} logo` : "Store logo"}
                  onError={() => setLogoFailed(true)} data-testid="site-logo"
                  className="h-9 w-auto max-w-[150px] shrink-0 object-contain" />
              ) : (
                /* Below sm the wordmark carries the identity, so the monogram tile is
                   dropped when a real business name is available — that buys the
                   44px the phone needs to keep the full name on one line. */
                <span data-testid="site-monogram"
                  className={`h-9 w-9 shrink-0 rounded-lg place-items-center font-display font-bold ${business?.name ? "hidden sm:grid" : "grid"} ${tone.monogram}`}>S</span>
              )}
              <span title={business?.name || undefined} data-testid="site-name"
                className={`font-display font-bold tracking-tight min-w-0 truncate leading-tight text-[12.5px] sm:text-base lg:text-lg ${tone.name}`}><BusinessName name={business?.name} loading={settingsLoading} /></span>
            </Link>

            <nav className={`hidden md:flex items-center gap-3 lg:gap-6 ml-2 lg:ml-4 text-sm font-medium ${tone.nav}`}>
              <Link to="/" className={`transition-colors ${tone.navHover}`} data-testid="nav-home">Home</Link>
              <Link to="/shop" className={`transition-colors ${tone.navHover}`} data-testid="nav-shop">Shop</Link>
              <Link to="/services" className={`transition-colors ${tone.navHover}`} data-testid="nav-services">Services</Link>
              <Link to="/categories" className={`transition-colors ${tone.navHover}`} data-testid="nav-categories">Categories</Link>
              <Link to="/brands" className={`transition-colors ${tone.navHover}`} data-testid="nav-brands">Brands</Link>
            </nav>

            <div className="hidden lg:block flex-1 max-w-md ml-auto">
              <SearchBox testid="desktop-search-input" overHero={overHero} q={q} setQ={setQ} suggest={suggest} boxRef={boxRef} submitSearch={submitSearch} navigate={navigate} setSuggest={setSuggest} />
            </div>

            <div className="flex items-center gap-1 sm:gap-2 ml-auto md:ml-0">
              <Link to="/wishlist" className={`relative p-1.5 sm:p-2.5 rounded-full transition-colors ${tone.iconHover}`} data-testid="wishlist-link" aria-label="Wishlist">
                <Heart className={`h-5 w-5 ${tone.icon}`} />
                {wishlist.length > 0 && <Badge>{wishlist.length}</Badge>}
              </Link>
              <Link to={user ? "/account" : "/login"} className={`p-2.5 rounded-full transition-colors hidden sm:block ${tone.iconHover}`} data-testid="account-link" aria-label="Account">
                <User className={`h-5 w-5 ${tone.icon}`} />
              </Link>
              <Link to="/cart" className={`relative p-1.5 sm:p-2.5 rounded-full transition-colors ${tone.iconHover}`} data-testid="cart-link" aria-label="Cart">
                <ShoppingBag className={`h-5 w-5 ${tone.icon}`} />
                {cartCount > 0 && <Badge>{cartCount}</Badge>}
              </Link>
            </div>
          </div>
          <div className="lg:hidden pb-3">
            <SearchBox testid="mobile-search-input" overHero={overHero} q={q} setQ={setQ} suggest={suggest} boxRef={boxRef} submitSearch={submitSearch} navigate={navigate} setSuggest={setSuggest} />
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setMenuOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-80 max-w-[85%] bg-white shadow-2xl flex flex-col animate-fade-up">
            <div className="flex items-center justify-between p-4 border-b">
              <span className="font-display font-bold text-lg"><BusinessName name={business?.name} loading={settingsLoading} /></span>
              <button onClick={() => setMenuOpen(false)} className="p-2" data-testid="mobile-menu-close"><X className="h-5 w-5" /></button>
            </div>
            <nav className="flex-1 overflow-y-auto p-2">
              {[["Home", "/"], ["Shop", "/shop"], ["Services", "/services"], ["Brands", "/brands"], ["Track Order", "/track-order"], ["About", "/about"], ["Contact", "/contact"]].map(([l, to]) => (
                <Link key={to} to={to} onClick={() => setMenuOpen(false)} className="flex items-center justify-between px-3 py-3 rounded-lg hover:bg-slate-50 font-medium text-slate-800">
                  {l} <ChevronRight className="h-4 w-4 text-slate-400" />
                </Link>
              ))}
              <div className="px-3 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Categories</div>
              {cats.map((c) => (
                <Link key={c.id} to={`/category/${c.slug}`} onClick={() => setMenuOpen(false)} className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700">
                  {c.name} <ChevronRight className="h-4 w-4 text-slate-300" />
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}
    </>
  );
}

const Badge = ({ children }) => (
  <span className="absolute -top-0.5 -right-0.5 h-4.5 min-w-[18px] px-1 grid place-items-center rounded-full bg-amber-500 text-white text-[10px] font-bold leading-none" style={{ height: 18 }}>
    {children}
  </span>
);
